import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {BluechatLoad} from '../../shared/bluechat-load.model';
import {ModalService} from '../../../shared/services/modal.service';
import {Papa, ParseResult} from 'ngx-papaparse';
import {CsvDataWithFieldName} from '../../../shared/CsvDataWithFieldName';
import {BluechatService} from '../../bluechat.service';
import {LoadManagementService} from '../../shared/load-management.service';
import {BluechatFindCarrierByDeliveryNumber} from '../../models/BluechatFindCarrierByDeliveryNumber';
import { finalize } from 'rxjs/operators';
import { BluechatNoteModalDelivery } from '../../models/BluechatNoteModalDelivery';
import { AuthenticationService } from '../../../shared/services/authentication.service';

@Component({
  selector: 'bluechat-mass-creation-note',
  templateUrl: './bluechat-mass-creation-note.component.html',
  styleUrls: ['./bluechat-mass-creation-note.component.scss']
})
export class BluechatMassCreationNoteComponent implements OnInit {

  @Input()
  loads: BluechatLoad[];

  @Output()
  creationFromListClicked = new EventEmitter<BluechatNoteModalDelivery[]>();

  @Output()
  creationFromCsvClicked = new EventEmitter<BluechatNoteModalDelivery[]>();

  selectedCsvFile: File;
  csv: CsvDataWithFieldName[];

  columns: string[];
  selectedColumn: string;
  parsingInProgress = false;
  carrierSearchError: boolean;
  incorrectDeliveriesBecauseNotKnown: BluechatFindCarrierByDeliveryNumber[];
  incorrectDeliveriesBecauseNoBluechat: BluechatFindCarrierByDeliveryNumber[];
  correctDeliveries: BluechatFindCarrierByDeliveryNumber[];

  public showCsvImport = false;

  private deliveryNumbers: string[];

  constructor(private modalService: ModalService,
              private authService: AuthenticationService,
              private bluechatService: BluechatService,
              private loadManagementService: LoadManagementService,
              private papa: Papa) { }

  public ngOnInit(): void {
    this.showCsvImport = this.authService.canSubmitAllBluechatNotes();
  }

  createFromList() {
    this.creationFromCsvClicked.emit(this.loadsToNoteModalDeliveries(this.loads));
    this.resetAllValues();
    this.modalService.triggerResolution();
  }

  changeSelectedCsv(csvFile: File) {
    this.selectedCsvFile = csvFile;
    this.parseCsv(csvFile);
  }

  deleteCsv(): void {
    this.csv = null;
    this.columns = null;
    this.selectedColumn = null;
    this.selectedCsvFile = null;
  }

  private parseCsv(csvFile: File) {
    this.papa.parse(csvFile, {
      header: true,
      skipEmptyLines: 'greedy',
      complete: (res: ParseResult) => {
        this.csv = res.data;
        this.columns = res.meta.fields;
        if (this.columns.length === 1) {
          this.selectedColumn = this.columns[0];
          this.updateDeliveryNumbers(this.selectedColumn);
        }
      }
    })
  }

  createFromCsv(): void {
    this.parsingInProgress = true;
    this.bluechatService.getBluchatFindByDeliveryNumbersLoad(this.deliveryNumbers)
      .pipe(
        finalize(() => this.parsingInProgress = false)
      ).subscribe(bluechatFindCarriersByDeliveryNumbers => {
        this.parsingInProgress = false;
        this.correctDeliveries = this.getCorrectDeliveries(bluechatFindCarriersByDeliveryNumbers);
        this.incorrectDeliveriesBecauseNoBluechat = this.getIncorrectDeliveriesBecauseBluechat(bluechatFindCarriersByDeliveryNumbers);
        this.incorrectDeliveriesBecauseNotKnown = this.getIncorrectDeliveriesBecauseNotKnown(bluechatFindCarriersByDeliveryNumbers);
        if (this.incorrectDeliveriesBecauseNoBluechat.length > 0 || this.incorrectDeliveriesBecauseNotKnown.length > 0) {
          this.carrierSearchError = true;
        } else {
          this.creationFromCsvClicked.emit(this.searchDeliveriesToNoteModalDeliveries(this.correctDeliveries));
          this.resetAllValues();
          this.modalService.triggerResolution();
        }
      });
  }

  createFromCsvForced(): void {
    this.creationFromCsvClicked.emit(this.searchDeliveriesToNoteModalDeliveries(this.correctDeliveries));
    this.resetAllValues();
    this.modalService.triggerResolution();
  }

  loadsToNoteModalDeliveries(loads: BluechatLoad[]): BluechatNoteModalDelivery[] {
    return loads.map(load => {
      return load.deliveries.map(delivery => {
        const modalDelivery = new BluechatNoteModalDelivery();

        modalDelivery.deliveryNumber = delivery.deliveryNumber;
        modalDelivery.carrierName = load.carrierName;
        modalDelivery.carrierBlueyonderCode = load.blueyonderCarrierCode;
        modalDelivery.carrierBluechatCode = load.bluechatCarrierCode;
        modalDelivery.carrierHasBluechatEnabled = load.bluechatCarrierCode != null;

        return modalDelivery;
      });
    }).reduce((el1, el2) => el1.concat(el2), []);
  }

  searchDeliveriesToNoteModalDeliveries(carriersDeliveries: BluechatFindCarrierByDeliveryNumber[]): BluechatNoteModalDelivery[] {
    return carriersDeliveries.map(carrierDeliveries => {
      return carrierDeliveries.deliveryNumbers.map(deliveryNumber => {
        const modalDelivery = new BluechatNoteModalDelivery();

        modalDelivery.deliveryNumber = deliveryNumber;
        modalDelivery.carrierName = carrierDeliveries.carrierName;
        modalDelivery.carrierBlueyonderCode = carrierDeliveries.carrierBlueyonderCode;
        modalDelivery.carrierBluechatCode = carrierDeliveries.carrierBluechatCode;
        modalDelivery.carrierHasBluechatEnabled = carrierDeliveries.bluechatEnabled;

        return modalDelivery;
      });
    }).reduce((el1, el2) => el1.concat(el2), []);
  }

  updateDeliveryNumbers(column: string): void {
    this.deliveryNumbers = [];
    this.csv.forEach(el => this.deliveryNumbers.push(el[column]));

    this.resetState();
  }

  private resetAllValues() {
    this.carrierSearchError              = false;
    this.correctDeliveries                    = [];
    this.incorrectDeliveriesBecauseNotKnown   = [];
    this.incorrectDeliveriesBecauseNoBluechat = [];
    this.deleteCsv();
  }

  private getCorrectDeliveries(source: BluechatFindCarrierByDeliveryNumber[]): BluechatFindCarrierByDeliveryNumber[] {
    return source
      .filter(value => value.existsOnCmaWeb && value.bluechatEnabled);
  }

  private getIncorrectDeliveriesBecauseBluechat(bluechatFindCarriersByDeliveryNumbers: BluechatFindCarrierByDeliveryNumber[]) {
    return bluechatFindCarriersByDeliveryNumbers.filter(el => el.bluechatEnabled === false && el.existsOnCmaWeb === true);
  }

  private getIncorrectDeliveriesBecauseNotKnown(bluechatFindCarriersByDeliveryNumbers: BluechatFindCarrierByDeliveryNumber[]) {
    return bluechatFindCarriersByDeliveryNumbers.filter(el => el.existsOnCmaWeb === false);
  }

  resetState(): void {
    this.incorrectDeliveriesBecauseNoBluechat = [];
    this.incorrectDeliveriesBecauseNotKnown = [];
    this.correctDeliveries = [];
    this.carrierSearchError = false;
  }
}
